import React, { useState } from "react";
import { Send, Mail, MapPin, Github, Linkedin } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";
import portfolio from "../data/portfolio.json";

const { personalInfo } = portfolio;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", company: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Could not send the message. Please try again.");
      }

      toast.success("Thanks. I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "", company: "" });
    } catch (error) {
      toast.error(error.message || "Could not send the message. Please email me directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-32 overflow-hidden">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px circle at 50% 100%, rgba(94,234,212,0.08), transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="flex items-center gap-4 mb-12">
          <span className="font-mono text-xs text-teal-300">05 / Contact</span>
          <div className="h-px flex-1 bg-gradient-to-r from-zinc-800 to-transparent" />
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <h2
              className="font-display font-bold text-white tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.25rem)" }}
            >
              Let's
              <span className="block text-teal-300">talk.</span>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Whether it's a freelance project, a contract role, or a quick
              question — the form below or a direct email both work. I typically
              reply within a day.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={`mailto:${personalInfo.email}`}
                className="flex items-center gap-4 p-4 rounded-2xl border border-zinc-800 hover:border-teal-300/40 hover:bg-white/[0.02] transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center group-hover:border-teal-300/40">
                  <Mail className="w-5 h-5 text-teal-300" />
                </div>
                <div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase">
                    Email
                  </div>
                  <div className="text-white">{personalInfo.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-2xl border border-zinc-800">
                <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-teal-300" />
                </div>
                <div>
                  <div className="text-[10px] font-mono text-zinc-500 uppercase">
                    Location
                  </div>
                  <div className="text-white">
                    {personalInfo.location} — working globally
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-2">
              {[
                { Icon: Github, href: "https://github.com/robertandros" },
                { Icon: Linkedin, href: "https://www.linkedin.com/in/robert-andros-a91266132/" },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="w-11 h-11 rounded-xl border border-zinc-800 hover:border-teal-300/40 flex items-center justify-center text-zinc-400 hover:text-teal-300 hover:bg-white/5 transition-all"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="glass-strong rounded-3xl p-8 gradient-border space-y-5"
            >
              <Input
                aria-hidden="true"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="hidden"
              />
              <div className="grid md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                    Your name *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Doe"
                    className="bg-zinc-950/60 border-zinc-800 text-white h-12 placeholder:text-zinc-600 focus-visible:ring-teal-300/40 focus-visible:border-teal-300/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jane@company.com"
                    className="bg-zinc-950/60 border-zinc-800 text-white h-12 placeholder:text-zinc-600 focus-visible:ring-teal-300/40 focus-visible:border-teal-300/40"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                  Subject
                </Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="Project inquiry, collab, etc."
                  className="bg-zinc-950/60 border-zinc-800 text-white h-12 placeholder:text-zinc-600 focus-visible:ring-teal-300/40 focus-visible:border-teal-300/40"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-zinc-400 text-xs font-mono uppercase tracking-wider">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="A few sentences about your project or question."
                  rows={6}
                  className="bg-zinc-950/60 border-zinc-800 text-white placeholder:text-zinc-600 focus-visible:ring-teal-300/40 focus-visible:border-teal-300/40 resize-none"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-zinc-500 font-mono">
                  I reply as soon as I can.
                </p>
                <Button
                  type="submit"
                  disabled={sending}
                  className="bg-teal-300 text-zinc-950 hover:bg-teal-200 h-12 px-6 font-medium rounded-xl group"
                >
                  {sending ? "Sending..." : "Send message"}
                  <Send className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
